/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
import React from 'react'
import { useParams } from 'react-router-dom'
import {
  Container,
  PatientInfoContainer,
  PatientInfoLabel,
  PatientInfoContent,
  PregnancyPatientPerspectiveEmptyContainer
} from './styles'
import { getAgeByBirthDate, transformDate } from '~/utils'
import { usePatient } from '~/context'
import { MaternalInfantTagOfSelectedTimeline } from '~/components/maternalInfantMedicalView'
import { Child } from '~/interfaces'
import { useFetchPatientPregnancies, useMaternalInfantViewSettings } from '~/hooks'
import { isPregnant } from '~/utils/dashboard/timeline/maternalInfant'
import { Skeleton, SkeletonStyleProps } from '~/components/shared'
import { Divider, theme } from '~/styles'

const MotherTag = ({ label }: { label: string }): JSX.Element => (
  <MaternalInfantTagOfSelectedTimeline patientType="mother">{label}</MaternalInfantTagOfSelectedTimeline>
)
const BabyTag = ({ label }: { label: string }): JSX.Element => (
  <MaternalInfantTagOfSelectedTimeline patientType="baby">{label}</MaternalInfantTagOfSelectedTimeline>
)

export const PregnancyPatientPerspective = ({
  visiblePatient
}: {
  visiblePatient: { medicalRecord: string }
}): JSX.Element => {
  const { medicalRecord } = useParams()
  const { patient, patientTag } = usePatient()

  const { data: patientPregnancies } = useFetchPatientPregnancies({ patientId: patient.id })
  const patientIsPregnant = patientPregnancies?.some(isPregnant)

  const isOriginalSearchedPatient = visiblePatient.medicalRecord === medicalRecord

  if (patientIsPregnant && isOriginalSearchedPatient) return <MotherTag label="Gestante" />
  if (patientTag && isOriginalSearchedPatient) return <MotherTag label={patientTag} />
  if (patientTag && !isOriginalSearchedPatient) return <BabyTag label={patientTag} />
  return <PregnancyPatientPerspectiveEmptyContainer />
}

interface PatientInfoProps {
  label: string
  content?: string | number
  isLoading: boolean
  loaderProps?: SkeletonStyleProps
}

const PatientInfo = ({ label, content, isLoading, loaderProps }: PatientInfoProps): JSX.Element => (
  <PatientInfoContainer>
    <PatientInfoLabel>{label}</PatientInfoLabel>
    <PatientInfoContent>
      {isLoading ? (
        <Skeleton baseColor={theme.colors.colore6ebf1} width={'80%'} {...loaderProps} />
      ) : (
        content || 'Não informado'
      )}
    </PatientInfoContent>
  </PatientInfoContainer>
)

interface PatientProfileProps {
  isCollapsed: boolean
}

export const PatientProfile = ({ isCollapsed }: PatientProfileProps): JSX.Element => {
  const { patient } = usePatient()
  const { getMaternalInfantViewSettings } = useMaternalInfantViewSettings()
  const { selectedBaby, selectedPregnancy, selectedTimeline } = getMaternalInfantViewSettings()
  const linkedPatient = selectedPregnancy?.children?.find(baby => baby?.id === selectedBaby) as Child
  const visiblePatient = selectedTimeline === 'baby' ? linkedPatient : patient

  const birthDateDescription = React.useMemo(() => {
    if (!visiblePatient?.birthDate) return undefined

    const age = getAgeByBirthDate(visiblePatient.birthDate)
    const ageDescription = `(${age} anos)`

    const birthDateAsddMMyyyy = transformDate({ date: visiblePatient.birthDate })
    return `${birthDateAsddMMyyyy} ${ageDescription}`
  }, [visiblePatient?.birthDate])

  return (
    <Container isCollapsed={isCollapsed}>
      {patient?.id ? (
        <PregnancyPatientPerspective visiblePatient={visiblePatient} />
      ) : (
        <PregnancyPatientPerspectiveEmptyContainer />
      )}

      <PatientInfo
        label="Prontuário"
        content={visiblePatient?.medicalRecord}
        isLoading={!patient?.id}
        loaderProps={{ width: '55%' }}
      />
      <Divider />
      <PatientInfo label="Nome" content={visiblePatient?.name} isLoading={!patient?.id} />
      <Divider />
      <PatientInfo label="Data de Nascimento" content={birthDateDescription} isLoading={!patient?.id} />
      <Divider />
      <PatientInfo label="Sexo" content={visiblePatient?.biologicalSex} isLoading={!patient?.id} />
      <Divider />
      <PatientInfo label="Tipo Sanguíneo" content={visiblePatient?.bloodType} isLoading={!patient?.id} />
    </Container>
  )
}
